import { Attachment } from "@/types/attachment";
import {
  ensureAttachmentPreview,
  validateAttachments,
} from "@/util/attachmentValidator";
import { getFilePreview } from "@/util/getFilePreview";
import { UploadFile, UploadProps } from "antd";
import { useCallback, useState } from "react";
import { useToast } from "../component/Toast/useToast";

const DEFAULT_MAX_UPLOAD_SIZE = 100 * 1024 * 1024; // 100MB
const DEFAULT_MAX_FILE_COUNT = 5;
const DEFAULT_ACCEPT = "image/*,video/*,audio/*";

export interface UseFileUploadOptions {
  accept?: string;
  maxSize?: number;
  maxCount?: number;
  onError?: (error: { tooMany?: boolean; oversized?: boolean }) => void;
}

/**
 * 通用文件上传 hook
 * 处理文件选择、验证、预览生成等逻辑
 */
export function useFileUpload(options: UseFileUploadOptions = {}) {
  const {
    accept = DEFAULT_ACCEPT,
    maxSize = DEFAULT_MAX_UPLOAD_SIZE,
    maxCount = DEFAULT_MAX_FILE_COUNT,
    onError,
  } = options;
  const { error: showErrorToast } = useToast({
    key: "file-upload-toast",
  });

  const [attachmentList, setAttachmentList] = useState<Attachment[]>([]);

  const uploadProps: UploadProps = {
    fileList: [],
    onChange: async ({ file: newFile }) => {
      const rawFile =
        (newFile as UploadFile).originFileObj ?? (newFile as unknown as File);

      const newAttachment: Attachment = {
        type: "file",
        preview: "",
        file: rawFile,
        id: newFile.uid,
      };

      let wasAccepted = false;

      setAttachmentList((prev) => {
        // 使用纯函数验证
        const { valid, errors } = validateAttachments(
          [...prev, newAttachment],
          {
            maxTotalSize: maxSize,
            maxFileCount: maxCount,
          }
        );

        // 检查新附件是否被接受
        wasAccepted = valid.some((a) => a.id === newAttachment.id);

        // 通知调用者有错误发生
        if (errors.tooMany || errors.oversized) {
          if (onError) {
            onError(errors);
          } else {
            if (errors.tooMany) {
              showErrorToast(
                `You can only upload up to ${maxCount} files at a time.`
              );
            } else if (errors.oversized) {
              showErrorToast(
                `The file size exceeds the maximum limit of ${
                  maxSize / 1024 / 1024
                }MB.`
              );
            }
          }
        }

        return valid;
      });

      // 只为被接受的附件生成预览，避免处理被拒绝的大文件
      if (wasAccepted) {
        try {
          const withPreview = await ensureAttachmentPreview(
            newAttachment,
            getFilePreview
          );
          setAttachmentList((current) =>
            current.map((item) =>
              item.id === withPreview.id ? withPreview : item
            )
          );
        } catch (error) {
          console.error("Failed to generate preview:", error);
          // 预览生成失败不影响附件本身，继续保留附件
        }
      }
    },
    beforeUpload: () => {
      return false; // 阻止自动上传
    },
    multiple: true,
    showUploadList: false,
    accept,
  };

  const onRemove = useCallback((attachment: Attachment) => {
    setAttachmentList((prev) =>
      prev.filter((item) => item.id !== attachment.id)
    );
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachmentList([]);
  }, []);

  return {
    attachmentList,
    setAttachmentList,
    uploadProps,
    onRemove,
    clearAttachments,
  };
}
